import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../hooks/useRole';
import { UserRole } from '../../utils/roleUtils';
import styled from 'styled-components';

const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7fafc;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7fafc;
  flex-direction: column;
  gap: 1rem;
`;

const ErrorTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
`;

const ErrorMessage = styled.p`
  font-size: 1rem;
  color: #64748b;
  margin: 0;
`;

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles: UserRole[];
  fallbackPath?: string;
}

const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({ 
  children, 
  requiredRoles,
  fallbackPath = '/'
}) => {
  const { isAuthenticated, loading } = useAuth();
  const { userRole, isAdmin } = useRole();

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Admin can access everything
  if (isAdmin) {
    return <>{children}</>;
  }

  // Check if user has required role
  const hasAccess = requiredRoles.includes(userRole as UserRole);

  if (!hasAccess) {
    return (
      <ErrorContainer>
        <ErrorTitle>Access Denied</ErrorTitle>
        <ErrorMessage>
          You don't have permission to access this page. Required role: {requiredRoles.join(' or ')}
        </ErrorMessage>
      </ErrorContainer>
    );
  }

  return <>{children}</>;
};

export default RoleProtectedRoute;

